import { useRef } from 'react';
import type { UseAdminProductCatalogArgs } from '../interfaces';
import { useAdminProductAvailabilityActions } from './useAdminProductAvailabilityActions';
import { useAdminProductCatalogFetch } from './useAdminProductCatalogFetch';
import { useAdminProductMaterialDetails } from './useAdminProductMaterialDetails';
import { useAdminProductMediaActions } from './useAdminProductMediaActions';
import { useAdminProductPriceActions } from './useAdminProductPriceActions';
import { useAdminProductSearchActions } from './useAdminProductSearchActions';
import { useAdminProductStockActions } from './useAdminProductStockActions';

export const useAdminProductCatalog = (args: UseAdminProductCatalogArgs) => {
  const productsRequestIdRef = useRef(0);
  const { fetchProducts } = useAdminProductCatalogFetch({
    productsRequestIdRef,
    setBaseProducts: args.setBaseProducts,
    setCurrentProductsPage: args.setCurrentProductsPage,
    setDraftPriceById: args.setDraftPriceById,
    setDraftStockById: args.setDraftStockById,
    setLoadingMoreProducts: args.setLoadingMoreProducts,
    setLoadingProducts: args.setLoadingProducts,
    setProducts: args.setProducts,
    setProductsHasMore: args.setProductsHasMore,
    setProductsSkip: args.setProductsSkip,
    setProductsTotal: args.setProductsTotal,
  });

  const materialDetails = useAdminProductMaterialDetails({
    loadingMaterialDetailsById: args.loadingMaterialDetailsById,
    products: args.products,
    setBaseProducts: args.setBaseProducts,
    setDraftPriceById: args.setDraftPriceById,
    setDraftStockById: args.setDraftStockById,
    setLoadingMaterialDetailsById: args.setLoadingMaterialDetailsById,
    setProducts: args.setProducts,
  });
  const stockActions = useAdminProductStockActions({
    activeSearchTerm: args.activeSearchTerm,
    draftStockById: args.draftStockById,
    fetchProducts,
    savingStockId: args.savingStockId,
    setBaseProducts: args.setBaseProducts,
    setDraftStockById: args.setDraftStockById,
    setProducts: args.setProducts,
    setSavingSizesId: args.setSavingSizesId,
    setSavingStockId: args.setSavingStockId,
  });
  const priceActions = useAdminProductPriceActions({
    draftPriceById: args.draftPriceById,
    savingPriceId: args.savingPriceId,
    setBaseProducts: args.setBaseProducts,
    setDraftPriceById: args.setDraftPriceById,
    setProducts: args.setProducts,
    setSavingPriceId: args.setSavingPriceId,
  });
  const availabilityActions = useAdminProductAvailabilityActions({
    setBaseProducts: args.setBaseProducts,
    setProducts: args.setProducts,
  });
  const mediaActions = useAdminProductMediaActions({
    activeSearchTerm: args.activeSearchTerm,
    fetchProducts,
  });
  const searchActions = useAdminProductSearchActions({
    activeSearchTerm: args.activeSearchTerm,
    fetchProducts,
    loadingMoreProducts: args.loadingMoreProducts,
    loadingProducts: args.loadingProducts,
    searchTerm: args.searchTerm,
    setActiveSearchTerm: args.setActiveSearchTerm,
    setBaseProducts: args.setBaseProducts,
    setDeletingProductId: args.setDeletingProductId,
    setProducts: args.setProducts,
    setProductsHasMore: args.setProductsHasMore,
    setSearchTerm: args.setSearchTerm,
    setSearchingProducts: args.setSearchingProducts,
  });

  return {
    fetchProducts,
    ...stockActions,
    ...priceActions,
    ...availabilityActions,
    ...mediaActions,
    ...searchActions,
    ...materialDetails,
  };
};
